/**
 * Communities Page Component
 * Main page for managing community pricing profiles
 */

import { useEffect, useState } from 'react';
import { RefreshCw, Plus, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/core/components/ui/button';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { ActorSelector } from '@/core/components/ActorSelector';
import { LocationSelector } from '@/core/components/LocationSelector';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAuthStore } from '@/core/stores/auth-store';
import { useActorStore } from '@/features/site-management/stores/actor-store';
import { useCommunitiesStore } from '../../stores/communities-store';
import { CommunitiesTable } from '../organisms/CommunitiesTable';
import { CommunityFormDialog } from '../organisms/CommunityFormDialog';
import { CommunitySearchBar } from '../molecules/CommunitySearchBar';
import { exportCommunitiesToExcel } from '../../utils/exportCommunities';
import { ProfileStatus, type CommunityProfile } from '../../types/communities-types';

export function CommunitiesPage() {
  console.log('🚀 CommunitiesPage: Component mounted');

  // Operator and location state
  const { selectedOperator, operators, isLoading: operatorsLoading, selectOperator, loadOperators } = useOperatorStore();
  const { isDistributor, isAuthenticated, operatorId: authOperatorId } = useAuthStore();
  const { actors, isLoading: actorsLoading, loadActorTree } = useActorStore();

  console.log('📋 CommunitiesPage: Current state', {
    isAuthenticated,
    isDistributor,
    selectedOperator: selectedOperator?.operatorId,
    operatorsCount: operators.length,
    authOperatorId,
  });
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [strategyFilter, setStrategyFilter] = useState<string>('all'); // all, 1, 2, 3
  const [includeExpired, setIncludeExpired] = useState<boolean>(false); // Show inactive communities (expired + deleted)

  const {
    communities,
    totalCount,
    isLoading,
    error,
    lastUpdated,
    filters,
    pageSizeOptions,
    loadCommunities,
    setFilters,
    setPage,
    setPageSize,
    updateCommunity,
    refresh,
  } = useCommunitiesStore();

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<CommunityProfile | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [communityToDelete, setCommunityToDelete] = useState<number | null>(null);

  // Load operators on mount for distributors
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !operatorsLoading) {
      console.log('📦 CommunitiesPage: Loading operators for distributor');
      loadOperators();
    }
  }, [isDistributor, operators.length, operatorsLoading, loadOperators]);

  // Auto-select operator when page loads with no selection (distributors only)
  // Note: Non-distributors are handled by ProtectedRoute which auto-selects from JWT
  useEffect(() => {
    console.log('🔄 CommunitiesPage: Auto-select effect running', {
      isAuthenticated,
      selectedOperator: selectedOperator?.operatorId,
      isDistributor,
      operatorsCount: operators.length,
    });

    // Only auto-select for distributors - ProtectedRoute handles non-distributors
    if (isAuthenticated && !selectedOperator && isDistributor && operators.length > 0) {
      console.log('✅ Auto-selecting first operator for distributor:', operators[0]?.operatorId);
      selectOperator(operators[0]);
    }
  }, [isAuthenticated, selectedOperator, isDistributor, operators, selectOperator]);

  // Load communities when location or include inactive filter changes
  useEffect(() => {
    // Wait for authentication to be ready before loading data
    if (isAuthenticated && selectedOperator) {
      const actorId = selectedLocation ? parseInt(selectedLocation) : null;
      console.log('📊 CommunitiesPage: Loading communities', {
        operatorId: selectedOperator.operatorId,
        actorId,
        includeExpired,
      });
      setFilters({ actorId, includeExpired });
      loadCommunities();
    } else {
      console.log('⏳ CommunitiesPage: Waiting for auth & operator', {
        isAuthenticated,
        selectedOperator: selectedOperator?.operatorId,
      });
    }
  }, [isAuthenticated, selectedOperator, selectedLocation, includeExpired, setFilters, loadCommunities]);

  const formatLastUpdated = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRefresh = () => {
    refresh();
  };

  const handleAddNew = () => {
    setSelectedCommunity(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (community: CommunityProfile) => {
    setSelectedCommunity(community);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    setCommunityToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (communityToDelete) {
      try {
        // Find the community to get all its fields
        const community = communities.find(c => c.profileId === communityToDelete);
        if (!community) {
          console.error('Community not found');
          toast.error('Community not found');
          return;
        }

        // Soft delete: mark as deleted (ProfileStatus.Deleted = 1)
        // Send ALL required fields to satisfy backend validation
        await updateCommunity(communityToDelete, {
          profileId: community.profileId,
          profileName: community.profileName,
          profileDescription: community.profileDescription,
          profileStatus: 1, // Mark as deleted (1=Deleted, 0=Active)
          strategyType: typeof community.strategyType === 'number' ? community.strategyType : parseInt(community.strategyType) || 1,
          singleDimensionValue: community.singleDimensionValue,
          strategySettings: community.strategySettings || '{}', // Required: provide default empty object if null
          startDate: community.startDate.split('T')[0], // Extract YYYY-MM-DD
          expirationDate: community.expirationDate ? community.expirationDate.split('T')[0] : null,
          processOrder: community.processOrder,
          updatedByName: community.updatedByName || 'System', // Required: provide default if null
        });
        toast.success(`Community "${community.profileName}" marked as inactive`);
        setDeleteDialogOpen(false);
        setCommunityToDelete(null);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to mark community as deleted';
        console.error('Failed to mark community as deleted:', error);
        toast.error(errorMessage);
      }
    }
  };

  // const handleIncludeExpiredChange = async (checked: boolean) => {
  //   setFilters({ includeExpired: checked, page: 1 }); // Reset to page 1 when filter changes
  //   // Wait for next tick to ensure state is updated
  //   await new Promise(resolve => setTimeout(resolve, 10));
  //   await loadCommunities();
  // };

  // Load actors when operator changes
  useEffect(() => {
    if (selectedOperator?.operatorId) {
      loadActorTree(selectedOperator.operatorId.toString());
    }
  }, [selectedOperator?.operatorId, loadActorTree]);

  const handleOperatorChange = (operatorId: string) => {
    const selectedOp = operators.find(op => op.operatorId.toString() === operatorId);
    if (selectedOp) {
      selectOperator(selectedOp);
    }
    // Reset location to "All Locations" when operator changes
    setSelectedLocation(undefined);
  };

  const handleLocationChange = (locationId?: string) => {
    setSelectedLocation(locationId);
  };

  const handleExport = () => {
    try {
      if (filteredCommunities.length === 0) {
        toast.error('No data to export');
        return;
      }
      exportCommunitiesToExcel(filteredCommunities, actors);
      toast.success(`Exported ${filteredCommunities.length} communities to Excel`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };


  // Filter communities by search query and strategy filter
  const filteredCommunities = communities.filter(community => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = community.profileName?.toLowerCase().includes(query);
      const descMatch = community.profileDescription?.toLowerCase().includes(query);
      if (!nameMatch && !descMatch) return false;
    }

    // Strategy filter
    if (strategyFilter !== 'all') {
      // Convert community strategyType to number for comparison
      let communityStrategyNum: number;

      if (typeof community.strategyType === 'number') {
        communityStrategyNum = community.strategyType;
      } else if (typeof community.strategyType === 'string') {
        // Parse string enum values or numeric strings
        const strategyMap: Record<string, number> = {
          'Percentage': 1,
          'FixedPercentage': 1,
          'FixedAmount': 2,
          'Fixed': 2,
          'ByKwh': 3,
          'Kwh': 3,
        };
        communityStrategyNum = strategyMap[community.strategyType] || parseInt(community.strategyType) || 0;
      } else {
        communityStrategyNum = 0;
      }

      // Compare numbers directly
      const filterNum = parseInt(strategyFilter);
      if (communityStrategyNum !== filterNum) return false;
    }

    return true;
  });

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Header - Fixed at top */}
      <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200">
        <div className="border-l-4 border-yellow-400 pl-4">
          <h1 className="text-lg font-semibold text-gray-900">Communities</h1>
        </div>
      </div>

      {/* Filters - Fixed below header */}
      <div className="flex-shrink-0 p-6 bg-white">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          {/* Operator Selector (only for distributors) */}
          {isDistributor && operators.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Operator</label>
              <ActorSelector
                operators={operators}
                selectedOperatorId={selectedOperator?.operatorId.toString() || ''}
                onOperatorChange={handleOperatorChange}
                isLoading={operatorsLoading}
                className="w-80"
              />
            </div>
          )}

          {/* Location Select with search */}
          {selectedOperator && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Location</label>
              <LocationSelector
                operatorId={selectedOperator.operatorId}
                selectedLocationId={selectedLocation}
                onLocationChange={handleLocationChange}
                isActorsLoading={actorsLoading}
                autoSelectFirst={false}
                skipApiCallOnActorChange={false}
                className="w-[280px]"
              />
            </div>
          )}

          {/* Include Expired Filter - Next to Location */}
          {/* {selectedOperator && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 opacity-0">Filter</label>
              <div className="flex items-center gap-2 h-10">
                <Checkbox
                  id="include-expired"
                  checked={filters.includeExpired}
                  onCheckedChange={handleIncludeExpiredChange}
                />
                <Label htmlFor="include-expired" className="text-sm cursor-pointer">
                  Include Expired
                </Label>
              </div>
            </div>
          )} */}

          {/* Strategy Filter */}
          <div className="flex flex-col gap-1 w-[200px]">
            <label className="text-sm font-medium text-gray-700">Strategy</label>
            <Select value={strategyFilter} onValueChange={setStrategyFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Strategies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Strategies</SelectItem>
                <SelectItem value="1">Energy Percentage</SelectItem>
                <SelectItem value="2">Total Percentage</SelectItem>
                <SelectItem value="3">Energy Amount</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Include Inactive Checkbox */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 opacity-0 pointer-events-none">Filter</label>
            <div className="flex items-center space-x-2 h-10">
              <Checkbox
                id="includeInactive"
                checked={includeExpired}
                onCheckedChange={(checked) => setIncludeExpired(checked === true)}
              />
              <Label
                htmlFor="includeInactive"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer whitespace-nowrap"
              >
                Include Inactive
              </Label>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex flex-col gap-1 w-[300px]">
            <label className="text-sm font-medium text-gray-700">Search</label>
            <CommunitySearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by Name or Description"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Right Side Buttons */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={isLoading || filteredCommunities.length === 0}
              className="h-8"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>

            <Button
              onClick={handleAddNew}
              size="sm"
              className="h-8 bg-yellow-400 hover:bg-yellow-500 text-black"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Community
            </Button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 mt-4">
            <p className="font-medium">Error loading communities</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}
      </div>

      {/* Content Area - Scrollable table */}
      <div className="flex-1 overflow-hidden px-6 bg-white">
        <CommunitiesTable
          communities={filteredCommunities}
          totalCount={searchQuery ? filteredCommunities.length : totalCount}
          currentPage={filters.page}
          pageSize={filters.pageSize}
          pageSizeOptions={pageSizeOptions}
          isLoading={isLoading}
          error={null} // Error already shown in banner
          actors={actors}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddNew={handleAddNew}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Community Inactive</AlertDialogTitle>
            <AlertDialogDescription>
              Mark this community as inactive? You can reactivate it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Mark Inactive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Community Form Dialog */}
      <CommunityFormDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        community={selectedCommunity}
        defaultActorId={selectedLocation ? parseInt(selectedLocation) : undefined}
      />
    </div>
  );
}
